import React, { useEffect, useState } from 'react';
import { Box, Button, Card, Container, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '../components/DataTable';
import PageTitle from '../components/PageTitle';
import Loader from '../components/loader';
import { ACTION_ICON_COLORS } from '../constants/iconActionColors';
import { INTRANET_PAGE_CONTAINER_SX } from '../constants/layout';
import { useConfirm } from '../assets/contexts/confirmContext';
import { useNotification } from '../assets/contexts/notificationContext';
import * as endPoints from '../assets/js/endpoints';

const EMPTY_FORM = { title: '', description: '', link: '' };

export default function ManageFormations() {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const confirm = useConfirm();
  const { showNotification } = useNotification();

  const fetchFormations = async () => {
    try {
      const data = await endPoints.getAllFormations();
      setFormations(Array.isArray(data) ? data : data?.data || []);
    } catch (_e) {
      showNotification('Error al cargar formaciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormations();
  }, []);

  const handleCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleEdit = (formation) => {
    setEditingId(formation.id);
    setForm({
      title: formation.title || '',
      description: formation.description || '',
      link: formation.link || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm('¿Eliminar formación?');
    if (!ok) return;

    try {
      await endPoints.deleteFormation(id);
      showNotification('Formación eliminada', 'success');
      fetchFormations();
    } catch (_e) {
      showNotification('Error eliminando formación', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      link: form.link.trim(),
    };

    try {
      if (editingId) {
        await endPoints.updateFormation(editingId, payload);
        showNotification('Formación actualizada', 'success');
      } else {
        await endPoints.createFormation(payload);
        showNotification('Formación creada', 'success');
      }
      setModalOpen(false);
      fetchFormations();
    } catch (_e) {
      showNotification('Error guardando formación', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <title>Gestión de Formaciones | CES</title>
      </Helmet>
      <Container maxWidth="xl" sx={INTRANET_PAGE_CONTAINER_SX}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <PageTitle>Gestión de Formaciones</PageTitle>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>Crear Formación</Button>
        </Box>

        <DataTable
          columns={[
            { id: 'title', label: 'Título' },
            { id: 'description', label: 'Descripción' },
            { id: 'link', label: 'Enlace' },
          ]}
          rows={formations}
          actions={(formation) => (
            <>
              <IconButton onClick={() => handleEdit(formation)} sx={{ color: ACTION_ICON_COLORS.edit }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(formation.id)} sx={{ color: ACTION_ICON_COLORS.delete }}>
                <DeleteIcon />
              </IconButton>
            </>
          )}
        />
      </Container>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Card sx={{ p: 3, m: '5% auto', width: { xs: '92%', sm: 560 } }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            {editingId ? 'Editar formación' : 'Crear formación'}
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Título" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField label="Descripción" multiline minRows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <TextField label="Enlace" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={saving}>{editingId ? 'Actualizar' : 'Crear'}</Button>
            </Box>
          </Box>
        </Card>
      </Modal>
    </>
  );
}
